import {
  TranscriptElement,
  TranscriptTextElement,
} from '@src/types.ts/transcript';
import { VideoEditor } from '../VideoEditor';
import { LegacyTranscript } from './LegacyTranscript';
import { IVideoEditor } from '../interface/IVideoEditor';

/**
 * Utility class for navigating and analyzing transcript continuity.
 * Provides methods for checking continuity between transcript segments
 * and finding elements within transcripts.
 */
export class TranscriptNavigator {
  private readonly transcript: LegacyTranscript;
  private readonly originalTranscription: ReadonlyArray<TranscriptElement>;

  /**
   * Creates a new TranscriptNavigator
   * @param transcript The transcript to navigate
   * @param editor Optional VideoEditor instance for timeline-related operations
   */
  constructor(transcript: LegacyTranscript) {
    this.transcript = transcript;
    this.originalTranscription = transcript.getOriginalTranscription();
  }

  /**
   * Checks if the range of indices contains only punctuation elements (or if it's empty).
   * @param startIdx Start index to check
   * @param endIdx End index to check
   * @param elements Optional transcript elements to check (defaults to original transcription)
   * @returns True if the range contains only punctuation or is empty
   */
  hasOnlyPunctuationBetween(
    startIdx: number,
    endIdx: number,
    elements?: ReadonlyArray<TranscriptElement>,
  ): boolean {
    if (startIdx > endIdx) return true; // Empty range

    const source = elements || this.originalTranscription;

    for (let i = startIdx; i <= endIdx; i++) {
      if (i >= source.length) break;
      const element = source[i];
      if (element && element.type !== 'punct') {
        return false;
      }
    }
    return true;
  }

  /**
   * Checks if two positions in the transcript are continuous.
   * Positions are considered continuous if they are adjacent or separated only by punctuation.
   * @param endIdx1 The ending index of the first position
   * @param startIdx2 The starting index of the second position
   * @param elements Optional transcript elements to check (defaults to original transcription)
   * @returns True if the positions are continuous
   */
  areIndicesContinuous(
    endIdx1: number,
    startIdx2: number,
    elements?: ReadonlyArray<TranscriptElement>,
  ): boolean {
    // Direct adjacency
    if (endIdx1 + 1 === startIdx2) return true;

    // Check if there are any text elements between the two indices
    return this.hasOnlyPunctuationBetween(endIdx1 + 1, startIdx2 - 1, elements);
  }

  /**
   * Finds the next text element from a starting position
   * @param startIdx Index to start searching from (inclusive)
   * @param elements Optional transcript elements to search (defaults to original transcription)
   * @returns The next text element or undefined if none found
   */
  findNextTextElement(
    startIdx: number,
    elements?: ReadonlyArray<TranscriptElement>,
  ): TranscriptTextElement | undefined {
    const source = elements || this.originalTranscription;

    for (let i = startIdx; i < source.length; i++) {
      const element = source[i];
      if (element && element.type === 'text') {
        return element as TranscriptTextElement;
      }
    }
    return undefined;
  }

  /**
   * Finds the previous text element from a starting position
   * @param startIdx Index to start searching from (inclusive)
   * @param elements Optional transcript elements to search (defaults to original transcription)
   * @returns The previous text element or undefined if none found
   */
  findPrevTextElement(
    startIdx: number,
    elements?: ReadonlyArray<TranscriptElement>,
  ): TranscriptTextElement | undefined {
    const source = elements || this.originalTranscription;

    for (let i = startIdx; i >= 0; i--) {
      const element = source[i];
      if (element && element.type === 'text') {
        return element as TranscriptTextElement;
      }
    }
    return undefined;
  }

  /**
   * Gets all text elements in the specified range
   * @param startIdx Starting index (inclusive)
   * @param endIdx Ending index (inclusive)
   * @param elements Optional transcript elements to search (defaults to original transcription)
   * @returns Array of text elements in the range
   */
  getTextElementsInRange(
    startIdx: number,
    endIdx: number,
    elements?: ReadonlyArray<TranscriptElement>,
  ): TranscriptTextElement[] {
    const source = elements || this.originalTranscription;
    const results: TranscriptTextElement[] = [];

    for (let i = startIdx; i <= endIdx && i < source.length; i++) {
      const el = source[i];
      if (el && el.type === 'text') {
        results.push(el as TranscriptTextElement);
      }
    }

    return results.sort((a, b) => a.initial_index - b.initial_index);
  }

  /**
   * Gets the last text element from an array of transcript elements
   * @param elements Transcript elements to search
   * @returns The last text element or undefined if none found
   */
  getLastTextElement(
    elements: TranscriptElement[],
  ): TranscriptTextElement | undefined {
    const textElements = elements.filter(
      el => el.type === 'text',
    ) as TranscriptTextElement[];
    return textElements.length > 0
      ? textElements[textElements.length - 1]
      : undefined;
  }

  /**
   * Gets the first text element from an array of transcript elements
   * @param elements Transcript elements to search
   * @returns The first text element or undefined if none found
   */
  getFirstTextElement(
    elements: TranscriptElement[],
  ): TranscriptTextElement | undefined {
    const textElements = elements.filter(
      el => el.type === 'text',
    ) as TranscriptTextElement[];
    return textElements.length > 0 ? textElements[0] : undefined;
  }

  /**
   * Checks if a previous segment and a range of indices are continuous
   * @param prevSegment The previous segment elements
   * @param startInitialIdx The starting index of the range
   * @returns True if the segment and range are continuous
   */
  isPrevSegmentContinuousWithRange(
    prevSegment: TranscriptElement[],
    startInitialIdx: number,
  ): boolean {
    const lastElement = this.getLastTextElement(prevSegment);
    if (!lastElement) return false;

    return this.areIndicesContinuous(
      lastElement.initial_index,
      startInitialIdx,
    );
  }

  /**
   * Checks if a range of indices and a next segment are continuous
   * @param endInitialIdx The ending index of the range
   * @param nextSegment The next segment elements
   * @returns True if the range and segment are continuous
   */
  isRangeContinuousWithNextSegment(
    endInitialIdx: number,
    nextSegment: TranscriptElement[],
  ): boolean {
    const firstElement = this.getFirstTextElement(nextSegment);
    if (!firstElement) return false;

    return this.areIndicesContinuous(endInitialIdx, firstElement.initial_index);
  }

  /**
   * Finds an element that matches the given filter by searching in a specific direction
   * @param startIdx Index to start searching from (inclusive)
   * @param elements Transcript elements to search through
   * @param filter Function that returns true for elements that match the criteria
   * @param direction Direction to search in ('left' to go backwards, 'right' to go forwards)
   * @returns Index of the first matching element, or -1 if none found
   */
  findElementIndexByFilter(
    startIdx: number,
    elements: TranscriptElement[],
    filter: (element: TranscriptElement, index: number) => boolean,
    direction: 'left' | 'right',
  ): number {
    let matchingIndex = -1;

    if (direction === 'left') {
      let idx = startIdx;
      while (idx >= 0) {
        if (filter(elements[idx], idx)) {
          matchingIndex = idx;
          break;
        }
        idx--;
      }
    } else {
      // direction === 'right'
      let idx = startIdx;
      while (idx < elements.length) {
        if (filter(elements[idx], idx)) {
          matchingIndex = idx;
          break;
        }
        idx++;
      }
    }

    return matchingIndex;
  }

  /**
   * Finds a valid transcript element and the corresponding timeline element
   * @param editor Video editor instance to use for timeline lookup
   * @param transcriptTrackId ID of the transcript track to search
   * @param startIdx Starting index in the transcript elements
   * @param elements Transcript elements to search
   * @param direction Direction to search ('left' for previous, 'right' for next)
   * @returns Object with elementId of the timeline element, or null if not found
   */
  findAdjacentTimelineElementId(
    editor: IVideoEditor,
    transcriptTrackId: string,
    startIdx: number,
    elements: TranscriptElement[],
    direction: 'left' | 'right',
  ): string | null {
    // Define filter for valid elements (not removed/cut, has timestamp, is text)
    const validElementFilter = (el: TranscriptElement) =>
      el.type === 'text' &&
      el.state !== 'removed' &&
      el.state !== 'cut' &&
      el.ts !== undefined;

    // Find the next valid element in the specified direction
    const idx = this.findElementIndexByFilter(
      startIdx,
      elements,
      validElementFilter,
      direction,
    );

    // If a valid element is found, look up the corresponding timeline element
    if (idx !== -1) {
      const ts =
        (elements[idx].ts as number) + (direction === 'right' ? 1e-6 : -1e-6); // Add a small buffer to avoid rounding issues
      const timelineElement = editor.getElementAtTime(transcriptTrackId, ts);
      if (timelineElement) {
        return timelineElement.id;
      }
    }

    return null;
  }

  /**
   * Finds the timeline elements adjacent to a range for restore operations.
   * High-level utility for VideoCreatorStore to use.
   *
   * @param editor Video editor instance
   * @param startIndex Starting index of the range to restore
   * @param endIndex Ending index of the range to restore
   * @param transcriptElements Full array of transcript elements
   * @returns Object with prevElementId and nextElementId
   */
  findAdjacentTimelineElementIds(
    editor: IVideoEditor,
    startIndex: number,
    endIndex: number,
    transcriptElements: TranscriptElement[],
  ): { prevElementId: string | null; nextElementId: string | null } {
    const transcriptTrack = editor.getTranscriptTrack();
    if (!transcriptTrack) {
      console.error('TranscriptNavigator: No transcript track found');
      return { prevElementId: null, nextElementId: null };
    }

    // Find previous timeline element (search left from startIndex - 1)
    const prevElementId = this.findAdjacentTimelineElementId(
      editor,
      transcriptTrack.id,
      startIndex - 1,
      transcriptElements,
      'left',
    );

    // Find next timeline element (search right from endIndex + 1)
    const nextElementId = this.findAdjacentTimelineElementId(
      editor,
      transcriptTrack.id,
      endIndex + 1,
      transcriptElements,
      'right',
    );

    return { prevElementId, nextElementId };
  }

  /**
   * Groups transcript elements by their corresponding timeline elements
   * @param transcriptElements Array of transcript elements to group
   * @param editor Video editor instance for timeline lookup
   * @param videoTrackId ID of the video track to search
   * @returns Record mapping timeline element IDs to arrays of transcript elements
   */
  groupTranscriptElementsByTimelineElement(
    transcriptElements: TranscriptElement[],
    editor: IVideoEditor,
    videoTrackId: string,
  ): Record<string, TranscriptElement[]> {
    // Iterate through the transcript elements and group them by their timeline element
    const groupedElements: Record<string, TranscriptElement[]> = {};

    // Filter to elements with timestamps (skip punctuation)
    const validElements = transcriptElements.filter(
      element => element.ts !== undefined,
    );

    // Group elements by timeline element
    validElements.forEach(element => {
      // Make sure we have a valid number before passing to getElementAtTime
      if (element.ts === undefined || element.ts === null) {
        return; // Skip this element
      }

      const ts = element.ts as number;
      const timelineElement = editor.getElementAtTime(videoTrackId, ts);
      if (timelineElement) {
        const timelineElementId = timelineElement.id;
        if (!groupedElements[timelineElementId]) {
          groupedElements[timelineElementId] = [];
        }
        groupedElements[timelineElementId].push(element);
      } else {
        console.warn(
          `TranscriptNavigator: No timeline element found for transcript element with ts: ${ts}`,
        );
      }
    });

    return groupedElements;
  }
}
